import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { Tag, Button } from "antd";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import BreadCrumb from "../Header/SecondHeader";
import AccessDenied from "../AccessDenied";
import Actions from "../antd/Table/Actions";
import FormRequest from "../Requests/FormRequest/FormRequest";
import ViewRequest from "../Requests/ViewRequsetPopup/ViewRequest";
import { popupcontext } from "../../context/Popupscontext";
import usePermission from "../../hooks/usePermission";
import { formatDateString } from "../../JsHelpers/DateFormat";
import { useGetServiceRequestsQuery } from "../../Redux/service/Dispatcher/Dispatcher";
import { useGetAllSkusQuery } from "../../Redux/service/Sku/Sku";
import {
	useGetModelsByProductQuery,
	useGetVillagesByMarkazQuery,
} from "../../Redux/service/CallCenter/CallCenter";
import { EgyptGovernorates } from "../../constants";
import { getMarkazOptions } from "../../utils/CountryState/egyptMarkaz";
import DispatcherSearchBar from "./DispatcherSearchBar";
import OpenRequestsPickerModal from "./DispatcherFilters/OpenRequestsPickerModal";
import BulkAssignModal from "./DispatcherFilters/BulkAssignModal";
import ReviewAssignmentModal from "./DispatcherFilters/ReviewAssignmentModal";
import ShowTasksModal from "./DispatcherFilters/ShowTasksModal";
import ColumnsToggle from "./DispatcherFilters/ColumnsToggle";
import TasksForm from "../Tasks/Forms/TasksForm";
import { STATUS_OPTIONS } from "./DispatcherFilters/ColumnFilters";

const DISPATCH_STATUS_COLORS = {
	open: "default",
	assigned: "purple",
	pending: "orange",
	inProgress: "blue",
	completed: "green",
	closed: "cyan",
	cancelled: "red",
};

const DEFAULT_FILTERS = {
	search: "",
	status: [],
	dateFilter: undefined,
	dateFrom: undefined,
	dateTo: undefined,
	governorate: [],
	markaz: [],
	village: [],
	productId: [],
	modelId: [],
};

// Filters backed by antd's multi-select column filters — parsed from/synced
// to the URL as comma-separated lists, same as `status` already was.
const MULTI_SELECT_FILTER_KEYS = [
	"status",
	"governorate",
	"markaz",
	"village",
	"productId",
	"modelId",
];

function filtersFromSearchParams(searchParams) {
	const filters = { ...DEFAULT_FILTERS };
	for (const key of Object.keys(DEFAULT_FILTERS)) {
		if (!searchParams.has(key)) continue;
		filters[key] = MULTI_SELECT_FILTER_KEYS.includes(key)
			? searchParams.get(key).split(",").filter(Boolean)
			: searchParams.get(key);
	}
	return filters;
}

function arraysEqual(a = [], b = []) {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((v, i) => v === sortedB[i]);
}

// Columns/Show-Hide: which columns can be toggled (Actions stays pinned) and
// where the dispatcher's choice is persisted across sessions.
const COLUMN_DEFINITIONS = [
	{ key: "requestNumber", titleKey: "Request Number" },
	{ key: "status", titleKey: "Status" },
	{ key: "governorate", titleKey: "Governorate" },
	{ key: "markaz", titleKey: "Markaz/Qism" },
	{ key: "village", titleKey: "Village" },
	{ key: "address", titleKey: "Address" },
	{ key: "product", titleKey: "Product" },
	{ key: "model", titleKey: "Model" },
	{ key: "complaintSummary", titleKey: "Complaint Summary" },
	{ key: "preferredVisitTime", titleKey: "Preferred Visit Time" },
	{ key: "createdAt", titleKey: "Requested Date" },
	{ key: "currentAssignee", titleKey: "Current Assignee" },
];
const ALL_COLUMN_KEYS = COLUMN_DEFINITIONS.map((c) => c.key);
const COLUMN_VISIBILITY_STORAGE_KEY = "dispatcherVisibleColumns";

function loadVisibleColumnKeys() {
	try {
		const stored = JSON.parse(
			localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY),
		);
		if (Array.isArray(stored)) {
			const filtered = stored.filter((k) => ALL_COLUMN_KEYS.includes(k));
			if (filtered.length) return filtered;
		}
	} catch {
		// ignore malformed storage value
	}
	return ALL_COLUMN_KEYS;
}

const Dispatcher = () => {
	const { view, edit: canEdit, delete: canDelete } = usePermission("DISPATCH");
	const { deleteItem, setDeleteItem, setEdited, edited, currentRequestId } =
		useContext(popupcontext);
	const [searchParams, setSearchParams] = useSearchParams();

	const [filters, setFilters] = useState(() =>
		filtersFromSearchParams(searchParams),
	);
	const [currentPage, setCurrentPage] = useState(
		Number(searchParams.get("page")) || 1,
	);
	const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 25);
	const [sort, setSort] = useState({
		sortBy: searchParams.get("sortBy") || "requestedDate",
		sortOrder: searchParams.get("sortOrder") || "desc",
	});
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [bulkAssignStep, setBulkAssignStep] = useState(null); // null | "technician" | "review"
	const [pendingTechnician, setPendingTechnician] = useState(null); // {technicianUserId, technicianName, notes}
	// Requests targeted by the assign flow — either the checkbox selection (bulk
	// button) or a single row's "Assign" action, tracked separately so a
	// single-row assign never touches the unrelated checkbox selection.
	const [assignTargetIds, setAssignTargetIds] = useState([]);
	const [isBulkAssignFlow, setIsBulkAssignFlow] = useState(false);
	const [editRequestId, setEditRequestId] = useState(false);
	const [showTasksRequestId, setShowTasksRequestId] = useState(null);
	// Tracks which column's native antd filter dropdown was just interacted with,
	// so handleTableChange knows which key inside its `filters` argument to apply
	// (matches the pattern in ReportTables/inventory/ShelfMovementReportTable.jsx).
	const [activeFilterKey, setActiveFilterKey] = useState(null);
	const [visibleColumnKeys, setVisibleColumnKeys] = useState(
		loadVisibleColumnKeys,
	);

	const { data: skusData } = useGetAllSkusQuery();
	const productOptions = (skusData?.data || []).map((s) => ({
		label: s.skuName,
		value: String(s.id),
	}));
	const { data: modelsData } = useGetModelsByProductQuery(
		filters.productId?.length ? filters.productId.join(",") : undefined,
		{ skip: !filters.productId?.length },
	);
	const modelOptions = modelsData?.options || [];
	// No governorate/markaz passed — lists every village, same unscoped-full-list
	// pattern as the Product filter's useGetAllSkusQuery() above.
	const { data: villagesData } = useGetVillagesByMarkazQuery({});
	const villageOptions = (villagesData?.data || []).map((v) => ({
		label: v.name,
		value: v.id,
	}));
	// Markaz has no dedicated endpoint — options come from a static lookup by
	// governorate, so with multiple governorates selected we union their lists.
	const markazOptions = filters.governorate?.length
		? Array.from(
				new Map(
					filters.governorate
						.flatMap((g) => getMarkazOptions(g))
						.map((o) => [o.value, o]),
				).values(),
			)
		: [];

	const queryArgs = {
		...filters,
		status: filters.status?.length ? filters.status.join(",") : undefined,
		governorate: filters.governorate?.length
			? filters.governorate.join(",")
			: undefined,
		markaz: filters.markaz?.length ? filters.markaz.join(",") : undefined,
		village: filters.village?.length ? filters.village.join(",") : undefined,
		productId: filters.productId?.length
			? filters.productId.join(",")
			: undefined,
		modelId: filters.modelId?.length ? filters.modelId.join(",") : undefined,
		page: currentPage,
		limit,
		sortBy: sort.sortBy,
		sortOrder: sort.sortOrder,
	};

	const { data, isFetching, refetch } = useGetServiceRequestsQuery(queryArgs, {
		refetchOnMountOrArgChange: true,
	});

	// POIs behind the requests targeted for bulk-assign, used to pull in that
	// POI's other open requests in the picker modal before the technician step.
	const selectedPoiIds = useMemo(() => {
		const rows = (data?.data || []).filter((r) =>
			assignTargetIds.includes(r.id),
		);
		return [...new Set(rows.map((r) => r.poi?.id).filter(Boolean))];
	}, [data, assignTargetIds]);

	// Requests picked in the open-requests step that aren't on the currently
	// loaded table page — kept so ReviewAssignmentModal can still build a row
	// for them (see OpenRequestsPickerModal's onNext).
	const [pickedExtraRequests, setPickedExtraRequests] = useState([]);

	// Actions' shared Edit/Delete flow ("request" type) reports back through
	// popupcontext rather than a local callback — same wiring CallCenter.jsx
	// and Requests.jsx use to refresh their lists after either action.
	useEffect(() => {
		if (deleteItem.condition === true && deleteItem.value === "request") {
			refetch();
			if (data?.data.length === 1 && currentPage > 1) {
				setCurrentPage(currentPage - 1);
			}
			setDeleteItem({ condition: null, value: null });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		currentPage,
		deleteItem.condition,
		deleteItem.value,
		refetch,
		setDeleteItem,
		data?.data,
	]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
		}
		fetchdata();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edited]);

	const syncUrl = (nextFilters, nextPage, nextLimit, nextSort) => {
		const params = new URLSearchParams();
		Object.entries(nextFilters).forEach(([key, value]) => {
			if (
				value === undefined ||
				value === null ||
				value === "" ||
				(Array.isArray(value) && value.length === 0)
			)
				return;
			params.set(key, Array.isArray(value) ? value.join(",") : value);
		});
		params.set("page", nextPage);
		params.set("limit", nextLimit);
		params.set("sortBy", nextSort.sortBy);
		params.set("sortOrder", nextSort.sortOrder);
		setSearchParams(params, { replace: true });
	};

	const handleFiltersChange = (patch) => {
		const nextFilters = { ...filters, ...patch };
		setFilters(nextFilters);
		setCurrentPage(1);
		syncUrl(nextFilters, 1, limit, sort);
	};

	const handleSearchChange = (value) => {
		handleFiltersChange({ search: value });
	};

	const handlePageChange = (page) => {
		setCurrentPage(page);
		syncUrl(filters, page, limit, sort);
	};

	const handleLimitChange = (newLimit) => {
		setLimit(newLimit);
		setCurrentPage(1);
		syncUrl(filters, 1, newLimit, sort);
	};

	const handleColumnVisibilityChange = (nextKeys) => {
		setVisibleColumnKeys(nextKeys);
		localStorage.setItem(
			COLUMN_VISIBILITY_STORAGE_KEY,
			JSON.stringify(nextKeys),
		);
	};

	const SORT_FIELD_BY_COLUMN_KEY = {
		createdAt: "requestedDate",
		preferredVisitTime: "preferredVisitTime",
		requestNumber: "requestNumber",
	};

	const handleTableChange = (pagination, tableFilters, sorter) => {
		const nextSort = sorter.order
			? {
					sortBy: SORT_FIELD_BY_COLUMN_KEY[sorter.field] || "requestedDate",
					sortOrder: sorter.order === "ascend" ? "asc" : "desc",
				}
			: { sortBy: "requestedDate", sortOrder: "desc" };
		const sortChanged =
			nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder;
		setSort(nextSort);

		const newStatus =
			activeFilterKey === "status"
				? tableFilters?.status || []
				: filters.status;
		const newGovernorate =
			activeFilterKey === "governorate"
				? tableFilters?.governorate || []
				: filters.governorate;
		const governorateChanged = !arraysEqual(
			newGovernorate,
			filters.governorate,
		);
		const newMarkaz = governorateChanged
			? []
			: activeFilterKey === "markaz"
				? tableFilters?.markaz || []
				: filters.markaz;
		const newVillage =
			activeFilterKey === "village"
				? tableFilters?.village || []
				: filters.village;
		const newProductId =
			activeFilterKey === "product"
				? tableFilters?.product || []
				: filters.productId;
		const productChanged = !arraysEqual(newProductId, filters.productId);
		const newModelId = productChanged
			? []
			: activeFilterKey === "model"
				? tableFilters?.model || []
				: filters.modelId;

		const nextFilters = {
			...filters,
			status: newStatus,
			governorate: newGovernorate,
			markaz: newMarkaz,
			village: newVillage,
			productId: newProductId,
			modelId: newModelId,
		};

		const filtersChanged =
			!arraysEqual(newStatus, filters.status) ||
			governorateChanged ||
			!arraysEqual(newMarkaz, filters.markaz) ||
			!arraysEqual(newVillage, filters.village) ||
			productChanged ||
			!arraysEqual(newModelId, filters.modelId);

		setFilters(nextFilters);
		const targetPage =
			filtersChanged || sortChanged ? 1 : pagination?.current || currentPage;
		setCurrentPage(targetPage);
		syncUrl(nextFilters, targetPage, limit, nextSort);
	};

	const columns = [
		{
			title: trans("Request Number"),
			dataIndex: "requestNumber",
			key: "requestNumber",
			sorter: true,
			width: 160,
			fixed: true,
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 150,
			filters: STATUS_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.status?.length ? filters.status : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("status");
			},
			render: (status) => (
				<Tag
					color={DISPATCH_STATUS_COLORS[status] || "default"}
					className="text-capitalize"
				>
					{trans(status)}
				</Tag>
			),
		},
		{
			title: trans("Governorate"),
			dataIndex: "governorate",
			key: "governorate",
			width: 140,
			filters: EgyptGovernorates.map((g) => ({
				text: g.label,
				value: g.value,
			})),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.governorate?.length ? filters.governorate : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("governorate");
			},
			render: (v) => (v ? trans(v) : "-"),
		},
		{
			title: trans("Markaz/Qism"),
			dataIndex: "markaz",
			key: "markaz",
			width: 140,
			filters: markazOptions.length
				? markazOptions.map((o) => ({ text: o.label, value: o.value }))
				: undefined,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.markaz?.length ? filters.markaz : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("markaz");
			},
			render: (v) => (v ? trans(v) : "-"),
		},
		{
			title: trans("Village"),
			dataIndex: "village",
			key: "village",
			width: 160,
			filters: villageOptions.map((o) => ({ text: o.label, value: o.value })),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.village?.length ? filters.village : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("village");
			},
			render: (v) => v || "-",
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 180,
			ellipsis: true,
			render: (v) => v || "-",
		},
		{
			title: trans("Product"),
			dataIndex: "product",
			key: "product",
			width: 150,
			filters: productOptions.map((o) => ({ text: o.label, value: o.value })),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.productId?.length ? filters.productId : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("product");
			},
			render: (v) => v || "-",
		},
		{
			title: trans("Model"),
			dataIndex: "model",
			key: "model",
			width: 140,
			filters: filters.productId?.length
				? modelOptions.map((o) => ({ text: o.label, value: o.value }))
				: undefined,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filters.modelId?.length ? filters.modelId : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("model");
			},
			render: (v) => v || "-",
		},
		{
			title: trans("Complaint Summary"),
			dataIndex: "complaintSummary",
			key: "complaintSummary",
			width: 220,
			ellipsis: true,
		},
		{
			title: trans("Preferred Visit Time"),
			dataIndex: "preferredVisitTime",
			key: "preferredVisitTime",
			width: 260,
			sorter: true,
			render: (v, record) => {
				if (!v) return "-";
				return record.preferredVisitTimeEnd
					? `${formatDateString(v)} - ${formatDateString(record.preferredVisitTimeEnd)}`
					: formatDateString(v);
			},
		},
		{
			title: trans("Requested Date"),
			dataIndex: "requestedDate",
			key: "createdAt",
			width: 190,
			sorter: true,
			render: (v) => formatDateString(v),
		},
		{
			title: trans("Current Assignee"),
			dataIndex: "currentAssignee",
			key: "currentAssignee",
			width: 150,
			render: (assignee) => assignee?.name || trans("Unassigned"),
		},
		{
			title: trans("Actions"),
			key: "actions",
			width: 150,
			fixed: !isRTL ? "right" : undefined,
			render: (_, record) => (
				<Actions
					request={{ id: record.id, status: record.status }}
					type="request"
					setEditRequest={setEditRequestId}
					canEdit={canEdit}
					canDelete={canDelete}
					onAssignTechnician={() => {
						setAssignTargetIds([record.id]);
						setPickedExtraRequests([]);
						setIsBulkAssignFlow(false);
						setBulkAssignStep(
							record.status === "open" ? "openRequests" : "technician",
						);
					}}
					onShowTasks={() => setShowTasksRequestId(record.id)}
				/>
			),
		},
	];

	const visibleColumns = columns.filter(
		(col) => col.key === "actions" || visibleColumnKeys.includes(col.key),
	);

	if (!view) {
		return <AccessDenied />;
	}

	return (
		<>
			<Helmet>
				<title>Dispatcher</title>
				<meta name="description" content="Dispatcher" />
			</Helmet>
			<BreadCrumb
				secButton={trans("Refresh")}
				secClick={() => refetch()}
				secLoading={isFetching}
				extra={
					selectedRowKeys.length > 0 && (
						<div className="d-flex align-items-center gap-2">
							{/* <Tag color="blue">
                {selectedRowKeys.length} {trans("Requests Selected")}
              </Tag> */}
							<Button
								type="primary"
								onClick={() => {
									const rows = (data?.data || []).filter((r) =>
										selectedRowKeys.includes(r.id),
									);
									const allOpen =
										rows.length > 0 && rows.every((r) => r.status === "open");
									setAssignTargetIds(selectedRowKeys);
									setPickedExtraRequests([]);
									setIsBulkAssignFlow(true);
									setBulkAssignStep(allOpen ? "openRequests" : "technician");
								}}
							>
								{trans("Assign to Technician")}
							</Button>
						</div>
					)
				}
			/>
			<DispatcherSearchBar
				value={filters.search}
				onChange={handleSearchChange}
				rightSlot={
					<>
						<span className="fw-500">
							{trans("Total Requests")}: {data?.pagination?.totalItems ?? 0}
						</span>
						<ColumnsToggle
							columns={COLUMN_DEFINITIONS.map((c) => ({
								key: c.key,
								title: trans(c.titleKey),
							}))}
							visibleKeys={visibleColumnKeys}
							onChange={handleColumnVisibilityChange}
						/>
					</>
				}
			/>

			<div className="bg-white-rounded-top mt-3 table-parent">
				<AntdTable
					columns={visibleColumns}
					loading={isFetching}
					dataSource={data?.data || []}
					pageSize={limit}
					totalItems={data?.pagination?.totalItems || 0}
					currentPage={currentPage}
					onPageChange={handlePageChange}
					onLimitChange={handleLimitChange}
					onTableChange={handleTableChange}
					scrollX={true}
					rowSelection={{
						selectedRowKeys,
						onChange: setSelectedRowKeys,
						getCheckboxProps: (record) => ({
							disabled:
								record.status === "cancelled" ||
								record.status === "closed" ||
								!record.poi,
						}),
					}}
				/>
			</div>

			<OpenRequestsPickerModal
				open={bulkAssignStep === "openRequests"}
				onClose={() => setBulkAssignStep(null)}
				poiIds={selectedPoiIds}
				initialSelectedIds={assignTargetIds}
				onNext={(mergedIds, extraRequests) => {
					setAssignTargetIds(mergedIds);
					setPickedExtraRequests(extraRequests);
					setBulkAssignStep("technician");
				}}
			/>
			<BulkAssignModal
				open={bulkAssignStep === "technician"}
				onClose={() => setBulkAssignStep(null)}
				selectedIds={assignTargetIds}
				onContinue={(technicianInfo) => {
					setPendingTechnician(technicianInfo);
					setBulkAssignStep("review");
				}}
			/>
			<ReviewAssignmentModal
				open={bulkAssignStep === "review"}
				onClose={() => setBulkAssignStep(null)}
				onBack={() => setBulkAssignStep("technician")}
				technicianUserId={pendingTechnician?.technicianUserId}
				technicianName={pendingTechnician?.technicianName}
				notes={pendingTechnician?.notes}
				selectedRows={assignTargetIds
					.map(
						(id) =>
							(data?.data || []).find((r) => r.id === id) ||
							pickedExtraRequests.find((r) => r.id === id),
					)
					.filter(Boolean)}
				onAssigned={() => {
					if (isBulkAssignFlow) setSelectedRowKeys([]);
					setAssignTargetIds([]);
					setPickedExtraRequests([]);
					setIsBulkAssignFlow(false);
					setBulkAssignStep(null);
					setPendingTechnician(null);
					refetch();
				}}
			/>
			<FormRequest
				requestForm={editRequestId}
				setRequestForm={setEditRequestId}
				isEditing={typeof editRequestId === "number"}
			/>
			<ShowTasksModal
				open={!!showTasksRequestId}
				requestId={showTasksRequestId}
				onClose={() => setShowTasksRequestId(null)}
			/>
			<TasksForm />
			{currentRequestId &&
				!(data?.data || []).some((r) => r.id === currentRequestId) && (
					<ViewRequest requestId={currentRequestId} />
				)}
		</>
	);
};

export default Dispatcher;
